import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createBrokerCarrier,
  getBrokerCarrier,
  getBrokerCarrierStats,
  getBrokerCarriers,
  updateBrokerCarrier,
  type BrokerCarrierStats,
} from "@/lib/api/broker-parties";
import { AdaptiveSheet } from "@/components/ui/adaptive-sheet";
import { Alert, AlertContent } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { EmailInput } from "@/components/ui/email-input";
import {
  EmptyState as EmptyStateUI,
  Label,
  Text,
  Value,
} from "@/components/ui/text";
import { Icon } from "@/components/ui/icons";
import { PageLoadingOverlay } from "@/components/ui/page-loading-overlay";
import { PhoneInput } from "@/components/ui/phone-input";
import { Input, Textarea } from "@/components/ui/input";
import { ModalBody, ModalFooter, ModalHeader } from "@/components/ui/modal";
import { PageTitle } from "@/contexts/page-title-context";
import { CustomSelect } from "@/components/ui/custom-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { Tab, TabContainer, TabList } from "@/components/ui/tab";
import { Fab } from "@/components/ui/fab";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { FormGrid } from "@/components/ui/form-grid";
import { lazyComponent } from "@/lib/lazy-component";
const DatePicker = lazyComponent(() =>
  import("@/components/ui/date-picker").then((m) => ({
    default: m.DatePicker,
  })),
);
import { DividedList } from "@/components/ui/divided-list";
import { Surface } from "@/components/ui/surface";
import { Skeleton } from "@/components/ui/skeleton";
import { TableViewport } from "@/components/ui/table-viewport";
import { VStack } from "@/components/ui/v-stack";
import { HStack } from "@/components/ui/h-stack";
import { MasterDetailLayout } from "@/components/ui/master-detail-layout";
import { IconCircle } from "@/components/ui/icon-circle";
import { getTrucks } from "@/lib/api/trucks";
import { getDrivers } from "@/lib/api/drivers";
import { getTrailers } from "@/lib/api/trailers";
import { hasPermission } from "@/lib/permissions";
import { DEFAULT_PAGE_SIZE } from "@/hooks/use-pagination";
import { queryKeys } from "@/lib/query-keys";
import { TruckModal } from "@/components/forms/truck-modal";
import { DriverModal } from "@/components/forms/driver-modal";
import { TrailerModal } from "@/components/forms/trailer-modal";
import { SelectableRow } from "@/components/ui/selectable-row";
import { usePartyPage } from "@/hooks/use-party-page";
import { highlight } from "@/lib/highlight";
import { entitySearchableFields } from "@/lib/search/entity";

type CarrierDetailTab = "fleet" | "drivers" | "trailers";

function CarriersPage() {
  const { t: tCarriers } = useTranslation("carriers");
  const { t: tCommon } = useTranslation("common");
  const { t: tCommonPagination } = useTranslation("common.pagination");
  const { t: tFleet } = useTranslation("fleet");

  const {
    language,
    isCompact,
    getLocalizedName,
    isLoading,
    errorKind,
    page,
    totalPages,
    setPage,
    total: totalCarriers,
    data: items,
    filteredItems,
    searchQuery,
    setSearchFilter,
    stats: carrierStats,
    invalidate,
    selectedId: selectedCarrierId,
    setSelectedId: setSelectedCarrierId,
    selectedParty: selectedCarrier,
    activeTab: activeTabRaw,
    setActiveTab: setActiveTabRaw,
    dialog,
    openDialog,
    closeDialog,
    isPartyModalOpen: isCarrierModalOpen,
    isEditingParty: isEditingCarrier,
    partyForm: carrierForm,
    setPartyForm: setCarrierForm,
    partyFormLoading: carrierFormLoading,
    partyFormSaving: carrierFormSaving,
    partyFormError: carrierFormError,
    partyFormCountries: carrierFormCountries,
    partyFormCountriesLoading: carrierFormCountriesLoading,
    partyFormCitiesLoading: carrierFormCitiesLoading,
    countryOptions: carrierCountryOptions,
    cityOptions: carrierCityOptions,
    openCreateModal,
    openEditModal,
    handleSaveParty: handleSaveCarrier,
  } = usePartyPage<BrokerCarrierStats>({
    dialogType: "carrier",
    queryKey: queryKeys.carriers({ limit: DEFAULT_PAGE_SIZE, offset: 0 }),
    queryFn: async ({ limit, offset }) => {
      const [listResponse, statsResponse] = await Promise.all([
        getBrokerCarriers({ limit, offset }),
        getBrokerCarrierStats(),
      ]);
      return { list: listResponse, stats: statsResponse };
    },
    getPartyDetail: getBrokerCarrier,
    createParty: (form) =>
      createBrokerCarrier({
        name: form.name,
        email: form.email.trim() ? form.email.trim() : null,
        phone_number: form.phone.trim() ? form.phone.trim() : null,
        country_id: form.countryId,
        city_id: form.cityId,
        address: form.address,
        incorporation_date: form.incorporationDate,
      }),
    updateParty: (id, form) =>
      updateBrokerCarrier(id, {
        name: form.name,
        email: form.email.trim() ? form.email.trim() : null,
        phone_number: form.phone.trim() ? form.phone.trim() : null,
        country_id: form.countryId,
        city_id: form.cityId,
        address: form.address,
        incorporation_date: form.incorporationDate,
      }),
  });

  const queryClient = useQueryClient();
  const activeTab = (activeTabRaw || "fleet") as CarrierDetailTab;
  const setActiveTab = (tab: CarrierDetailTab) => setActiveTabRaw(tab);

  const trucksQuery = useQuery({
    queryKey: queryKeys.trucks({ transport_company_id: selectedCarrierId }),
    queryFn: () =>
      getTrucks({ transport_company_id: selectedCarrierId, limit: 50 }),
    enabled: !!selectedCarrierId,
  });
  const trucksData = trucksQuery.data;
  const trucks = useMemo(() => trucksData?.data ?? [], [trucksData]);
  const trucksLoading = trucksQuery.isLoading && !!selectedCarrierId;
  const trucksErrorKind = trucksQuery.isError ? ("load" as const) : null;

  const driversQuery = useQuery({
    queryKey: queryKeys.drivers({ transport_company_id: selectedCarrierId }),
    queryFn: () =>
      getDrivers({ transport_company_id: selectedCarrierId, limit: 50 }),
    enabled: !!selectedCarrierId,
  });
  const driversData = driversQuery.data;
  const drivers = useMemo(() => driversData?.data ?? [], [driversData]);
  const driversLoading = driversQuery.isLoading && !!selectedCarrierId;
  const driversErrorKind = driversQuery.isError ? ("load" as const) : null;

  const trailersQuery = useQuery({
    queryKey: queryKeys.trailers({ transport_company_id: selectedCarrierId }),
    queryFn: () =>
      getTrailers({ transport_company_id: selectedCarrierId, limit: 50 }),
    enabled: !!selectedCarrierId,
  });
  const trailersData = trailersQuery.data;
  const trailers = useMemo(() => trailersData?.data ?? [], [trailersData]);
  const trailersLoading = trailersQuery.isLoading && !!selectedCarrierId;
  const trailersErrorKind = trailersQuery.isError ? ("load" as const) : null;

  // Per-list search inputs are independent — splitting them avoids the
  // bundled-object dodge of the max-component-state rule and lets each
  // useMemo below depend on only the input it actually reads.
  const [truckSearch, setTruckSearch] = useState("");
  const [driverSearch, setDriverSearch] = useState("");
  const [trailerSearch, setTrailerSearch] = useState("");

  const canAddCarrier = hasPermission("AddTransportCompany");
  const canEditCarrier = hasPermission("EditTransportCompany");
  const canAddTruck = hasPermission("AddTruck");
  const canAddDriver = hasPermission("AddDriver");
  const canAddTrailer = hasPermission("AddTrailer");
  const canEditTruck = hasPermission("EditTruck");
  const canEditDriver = hasPermission("EditDriver");
  const canEditTrailer = hasPermission("EditTrailer");

  const truckModalOpen = dialog?.type === "truck";
  const truckModalId = dialog?.type === "truck" ? (dialog.id ?? null) : null;
  const driverModalOpen = dialog?.type === "driver";
  const driverModalId = dialog?.type === "driver" ? (dialog.id ?? null) : null;
  const trailerModalOpen = dialog?.type === "trailer";
  const trailerModalId =
    dialog?.type === "trailer" ? (dialog.id ?? null) : null;

  const invalidateFleet = () => {
    void queryClient.invalidateQueries({ queryKey: ["trucks"] });
    void queryClient.invalidateQueries({ queryKey: ["drivers"] });
    void queryClient.invalidateQueries({ queryKey: ["trailers"] });
    invalidate();
  };

  const filteredTrucks = useMemo(() => {
    if (!truckSearch) return trucks;
    const query = truckSearch.toLowerCase();
    return trucks.filter(
      (truck) =>
        truck.current_number.toLowerCase().includes(query) ||
        truck.model.name.toLowerCase().includes(query),
    );
  }, [truckSearch, trucks]);

  const filteredDrivers = useMemo(() => {
    if (!driverSearch) return drivers;
    const query = driverSearch.toLowerCase();
    return drivers.filter((driver) =>
      driver.name.toLowerCase().includes(query),
    );
  }, [driverSearch, drivers]);

  const filteredTrailers = useMemo(() => {
    if (!trailerSearch) return trailers;
    const query = trailerSearch.toLowerCase();
    return trailers.filter(
      (trailer) =>
        trailer.type.name.toLowerCase().includes(query) ||
        trailer.model.name.toLowerCase().includes(query) ||
        trailer.load_type.name.toLowerCase().includes(query),
    );
  }, [trailerSearch, trailers]);

  return (
    <VStack gap="lg" data-testid="broker-carriers-page">
      <PageTitle>{tCarriers("title")}</PageTitle>

      {errorKind && (
        <Alert data-testid="broker-carriers-error" color="danger">
          <AlertContent icon={<Icon.Alert size="md" />}>
            <Text color="inherit">
              {errorKind ? tCommon("errorLoadingData") : null}
            </Text>
          </AlertContent>
        </Alert>
      )}

      <MasterDetailLayout isCompact={isCompact}>
        <VStack
          gap="md"
          className={isCompact && selectedCarrierId ? "hidden" : ""}
        >
          <Card variant="bordered" padding="xs">
            <FilterToolbar
              searchPlaceholder={tCommon("search")}
              searchValue={searchQuery}
              onSearchChange={(v) => setSearchFilter(v)}
              createAction={
                canAddCarrier
                  ? {
                      label: tCarriers("addCarrier"),
                      onClick: openCreateModal,
                      testId: "carriers-add-btn",
                    }
                  : undefined
              }
              pagination={{
                page,
                totalPages,
                perPage: DEFAULT_PAGE_SIZE,
                onPageChange: setPage,
                loading: isLoading,
              }}
            />
          </Card>

          <Card data-testid="broker-carriers-card" padding="none">
            <TableViewport position="relative">
              {isLoading && items.length === 0 ? (
                <DividedList rounded className="p-3">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <VStack gap="sm" key={index} className="py-3">
                      <HStack justify="between" gap="none">
                        <Skeleton className="h-5 w-40" />
                        <Skeleton rounded="full" className="h-6 w-14" />
                      </HStack>
                      <Skeleton className="h-4 w-52" />
                    </VStack>
                  ))}
                </DividedList>
              ) : filteredItems.length === 0 ? (
                <EmptyStateUI
                  title={tCommon("noCarriers")}
                  icon={<Icon.Truck size="xl" />}
                  className="py-8"
                />
              ) : (
                <DividedList rounded>
                  {filteredItems.map((carrier) => {
                    const isSelected = carrier.id === selectedCarrierId;
                    return (
                      <SelectableRow
                        key={carrier.id}
                        selected={isSelected}
                        data-testid={`broker-carrier-row-${carrier.id}`}
                        onClick={() =>
                          setSelectedCarrierId((prev) =>
                            prev === carrier.id ? "" : carrier.id,
                          )
                        }
                      >
                        <HStack align="start" justify="between" gap="lg">
                          <VStack gap="sm">
                            <Text weight="semibold">
                              {highlight(
                                entitySearchableFields(carrier, language).name,
                                searchQuery,
                              )}
                            </Text>
                            <HStack className="text-[color:var(--on-surface-variant)]">
                              <Icon.MapPin size="sm" color="muted" />
                              <Text size="xs" color="muted">
                                {getLocalizedName(carrier.city, language)},{" "}
                                {getLocalizedName(carrier.country, language)}
                              </Text>
                            </HStack>
                          </VStack>
                          <Badge color="success" size="sm">
                            {tCarriers("active")}
                          </Badge>
                          {carrier.is_self ? (
                            <Badge color="info" size="sm">
                              {tCarriers("myCompanyYou")}
                            </Badge>
                          ) : null}
                        </HStack>
                      </SelectableRow>
                    );
                  })}
                </DividedList>
              )}
              {isLoading && items.length > 0 && (
                <PageLoadingOverlay
                  page={page}
                  label={tCommonPagination("loadingPage")}
                />
              )}
            </TableViewport>
          </Card>
        </VStack>

        <Card
          data-testid="broker-carrier-detail"
          padding="none"
          className={isCompact && !selectedCarrierId ? "hidden" : ""}
        >
          {isCompact && selectedCarrier ? (
            // eslint-disable-next-line design-tokens/no-raw-html-elements -- back navigation button with border-bottom divider, not a standard Button variant
            <button
              type="button"
              onClick={() => setSelectedCarrierId("")}
              className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-[color:var(--primary)] hover:bg-[color:var(--surface-container)] transition-colors w-full border-b border-[color:var(--outline-variant)]"
              data-testid="carrier-detail-back"
            >
              <Icon.ArrowLeft size="sm" />
              {tCommon("back")}
            </button>
          ) : null}
          {!selectedCarrier ? (
            <VStack gap="none" padding="xl">
              <VStack align="center" className="text-center">
                <IconCircle size="xl" color="primary">
                  <Icon.Dashboard size="lg" color="primary" />
                </IconCircle>
                <Text weight="medium">{tCarriers("carrierNetwork")}</Text>
                <Text size="xs" color="muted">
                  {tCarriers("selectFromList")}
                </Text>
              </VStack>
              <FormGrid cols="sm-3" gap="lg" className="mt-8">
                <Surface border="default" padding="lg" className="text-center">
                  <Text size="xs" color="muted">
                    {tCarriers("totalCarriers")}
                  </Text>
                  <Value size="lg" weight="semibold">
                    {totalCarriers}
                  </Value>
                </Surface>
                <Surface border="default" padding="lg" className="text-center">
                  <Text size="xs" color="muted">
                    {tCarriers("totalFleet")}
                  </Text>
                  <Value size="lg" weight="semibold">
                    {carrierStats?.total_trucks ?? "—"}
                  </Value>
                </Surface>
                <Surface border="default" padding="lg" className="text-center">
                  <Text size="xs" color="muted">
                    {tCarriers("totalDrivers")}
                  </Text>
                  <Value size="lg" weight="semibold">
                    {carrierStats?.total_drivers ?? "—"}
                  </Value>
                </Surface>
              </FormGrid>
            </VStack>
          ) : (
            <VStack gap="lg" padding="md">
              <Surface border="default" padding="none">
                <Surface border="bottom">
                  <VStack gap="lg" className="p-4">
                    <VStack className="sm:flex-row sm:items-start sm:justify-between">
                      <VStack gap="sm">
                        <HStack>
                          <Value size="lg" weight="semibold">
                            {selectedCarrier.name}
                          </Value>
                          <Badge color="success" size="sm">
                            {tCarriers("active")}
                          </Badge>
                          {selectedCarrier.is_self ? (
                            <Badge color="info" size="sm">
                              {tCarriers("myCompanyYou")}
                            </Badge>
                          ) : null}
                        </HStack>
                      </VStack>
                      {canEditCarrier ? (
                        <Button
                          color="neutral"
                          variant="outline"
                          size="md"
                          onClick={() => openEditModal(selectedCarrier)}
                        >
                          {tCarriers("editProfile")}
                        </Button>
                      ) : null}
                    </VStack>
                    <FormGrid>
                      <VStack>
                        <HStack gap="lg">
                          <Icon.Phone size="sm" color="muted" />
                          <Text>{selectedCarrier.phone_number || "-"}</Text>
                        </HStack>
                        <HStack gap="lg">
                          <Icon.Email size="sm" color="muted" />
                          <Text>{selectedCarrier.email || "-"}</Text>
                        </HStack>
                      </VStack>
                      <VStack>
                        <HStack gap="lg">
                          <Icon.MapPin size="sm" color="muted" />
                          <Text>
                            {getLocalizedName(selectedCarrier.city, language)},{" "}
                            {getLocalizedName(
                              selectedCarrier.country,
                              language,
                            )}
                          </Text>
                        </HStack>
                        <HStack gap="lg">
                          <Icon.Location size="sm" color="muted" />
                          <Text>{selectedCarrier.address || "-"}</Text>
                        </HStack>
                      </VStack>
                    </FormGrid>
                  </VStack>
                </Surface>
              </Surface>

              <TabContainer padding="none" className="px-1">
                <TabList className="px-3">
                  <Tab
                    active={activeTab === "fleet"}
                    onClick={() => setActiveTab("fleet")}
                  >
                    {tFleet("trucks")} ({trucks.length})
                  </Tab>
                  <Tab
                    active={activeTab === "drivers"}
                    onClick={() => setActiveTab("drivers")}
                  >
                    {tFleet("drivers")} ({drivers.length})
                  </Tab>
                  <Tab
                    active={activeTab === "trailers"}
                    onClick={() => setActiveTab("trailers")}
                  >
                    {tFleet("trailers")} ({trailers.length})
                  </Tab>
                </TabList>
              </TabContainer>

              {activeTab === "fleet" ? (
                <VStack gap="lg">
                  <Card variant="bordered" padding="xs">
                    <FilterToolbar
                      searchPlaceholder={tCommon("search")}
                      searchValue={truckSearch}
                      onSearchChange={(v) => setTruckSearch(v)}
                      createAction={
                        canAddTruck
                          ? {
                              label: tFleet("addTruck"),
                              onClick: () => {
                                openDialog("truck");
                              },
                            }
                          : undefined
                      }
                    />
                  </Card>

                  {trucksLoading ? (
                    <TableViewport>
                      <Table data-testid="carrier-trucks-skeleton">
                        <TableHead>
                          <TableRow>
                            <TableHeaderCell>
                              {tFleet("trucks")}
                            </TableHeaderCell>
                            <TableHeaderCell>{tCommon("name")}</TableHeaderCell>
                            {canEditTruck ? (
                              <TableHeaderCell>
                                {tCommon("actions")}
                              </TableHeaderCell>
                            ) : null}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Array.from({ length: 4 }).map((_, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Skeleton className="h-5 w-24" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-40" />
                              </TableCell>
                              {canEditTruck ? (
                                <TableCell>
                                  <Skeleton className="h-8 w-16" />
                                </TableCell>
                              ) : null}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableViewport>
                  ) : trucksErrorKind ? (
                    <Text color="danger">{tCommon("errorLoadingData")}</Text>
                  ) : filteredTrucks.length === 0 ? (
                    <EmptyStateUI
                      title={tCommon("noneYet")}
                      description={tFleet("trucks")}
                      icon={<Icon.Truck size="xl" />}
                      className="py-8"
                    />
                  ) : (
                    <TableViewport>
                      <Table data-testid="carrier-trucks-table">
                        <TableHead>
                          <TableRow>
                            <TableHeaderCell>
                              {tFleet("trucks")}
                            </TableHeaderCell>
                            <TableHeaderCell>{tCommon("name")}</TableHeaderCell>
                            {canEditTruck ? (
                              <TableHeaderCell>
                                {tCommon("actions")}
                              </TableHeaderCell>
                            ) : null}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredTrucks.map((truck) => (
                            <TableRow
                              key={truck.id}
                              hoverable
                              data-testid={`carrier-truck-${truck.id}`}
                            >
                              <TableCell>
                                <Text weight="medium">
                                  {truck.current_number}
                                </Text>
                              </TableCell>
                              <TableCell>
                                <Text size="xs" color="muted" block>
                                  {truck.model.name}
                                </Text>
                              </TableCell>
                              {canEditTruck ? (
                                <TableCell>
                                  <Button
                                    color="neutral"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      openDialog("truck", truck.id);
                                    }}
                                  >
                                    {tCommon("edit")}
                                  </Button>
                                </TableCell>
                              ) : null}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableViewport>
                  )}
                </VStack>
              ) : null}

              {activeTab === "drivers" ? (
                <VStack gap="lg">
                  <Card variant="bordered" padding="xs">
                    <FilterToolbar
                      searchPlaceholder={tCommon("search")}
                      searchValue={driverSearch}
                      onSearchChange={(v) => setDriverSearch(v)}
                      createAction={
                        canAddDriver
                          ? {
                              label: tFleet("addDriver"),
                              onClick: () => {
                                openDialog("driver");
                              },
                            }
                          : undefined
                      }
                    />
                  </Card>

                  {driversLoading ? (
                    <TableViewport>
                      <Table data-testid="carrier-drivers-skeleton">
                        <TableHead>
                          <TableRow>
                            <TableHeaderCell>{tCommon("name")}</TableHeaderCell>
                            {canEditDriver ? (
                              <TableHeaderCell>
                                {tCommon("actions")}
                              </TableHeaderCell>
                            ) : null}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Array.from({ length: 4 }).map((_, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Skeleton className="h-5 w-28" />
                              </TableCell>
                              {canEditDriver ? (
                                <TableCell>
                                  <Skeleton className="h-8 w-16" />
                                </TableCell>
                              ) : null}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableViewport>
                  ) : driversErrorKind ? (
                    <Text color="danger">{tCommon("errorLoadingData")}</Text>
                  ) : filteredDrivers.length === 0 ? (
                    <EmptyStateUI
                      title={tCommon("noneYet")}
                      description={tFleet("drivers")}
                      icon={<Icon.User size="xl" />}
                      className="py-8"
                    />
                  ) : (
                    <TableViewport>
                      <Table data-testid="carrier-drivers-table">
                        <TableHead>
                          <TableRow>
                            <TableHeaderCell>{tCommon("name")}</TableHeaderCell>
                            {canEditDriver ? (
                              <TableHeaderCell>
                                {tCommon("actions")}
                              </TableHeaderCell>
                            ) : null}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredDrivers.map((driver) => (
                            <TableRow
                              key={driver.id}
                              hoverable
                              data-testid={`carrier-driver-${driver.id}`}
                            >
                              <TableCell>
                                <Text weight="medium">{driver.name}</Text>
                              </TableCell>
                              {canEditDriver ? (
                                <TableCell>
                                  <Button
                                    color="neutral"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      openDialog("driver", driver.id);
                                    }}
                                  >
                                    {tCommon("edit")}
                                  </Button>
                                </TableCell>
                              ) : null}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableViewport>
                  )}
                </VStack>
              ) : null}

              {activeTab === "trailers" ? (
                <VStack gap="lg">
                  <Card variant="bordered" padding="xs">
                    <FilterToolbar
                      searchPlaceholder={tCommon("search")}
                      searchValue={trailerSearch}
                      onSearchChange={(v) => setTrailerSearch(v)}
                      createAction={
                        canAddTrailer
                          ? {
                              label: tFleet("addTrailer"),
                              onClick: () => {
                                openDialog("trailer");
                              },
                            }
                          : undefined
                      }
                    />
                  </Card>

                  {trailersLoading ? (
                    <TableViewport>
                      <Table data-testid="carrier-trailers-skeleton">
                        <TableHead>
                          <TableRow>
                            <TableHeaderCell>{tCommon("name")}</TableHeaderCell>
                            <TableHeaderCell>
                              {tFleet("trailers")}
                            </TableHeaderCell>
                            {canEditTrailer ? (
                              <TableHeaderCell>
                                {tCommon("actions")}
                              </TableHeaderCell>
                            ) : null}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Array.from({ length: 4 }).map((_, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Skeleton className="h-5 w-28" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-40" />
                              </TableCell>
                              {canEditTrailer ? (
                                <TableCell>
                                  <Skeleton className="h-8 w-16" />
                                </TableCell>
                              ) : null}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableViewport>
                  ) : trailersErrorKind ? (
                    <Text color="danger">{tCommon("errorLoadingData")}</Text>
                  ) : filteredTrailers.length === 0 ? (
                    <EmptyStateUI
                      title={tCommon("noneYet")}
                      description={tFleet("trailers")}
                      icon={<Icon.Archive size="xl" />}
                      className="py-8"
                    />
                  ) : (
                    <TableViewport>
                      <Table data-testid="carrier-trailers-table">
                        <TableHead>
                          <TableRow>
                            <TableHeaderCell>{tCommon("name")}</TableHeaderCell>
                            <TableHeaderCell>
                              {tFleet("trailers")}
                            </TableHeaderCell>
                            {canEditTrailer ? (
                              <TableHeaderCell>
                                {tCommon("actions")}
                              </TableHeaderCell>
                            ) : null}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredTrailers.map((trailer) => (
                            <TableRow
                              key={trailer.id}
                              hoverable
                              data-testid={`carrier-trailer-${trailer.id}`}
                            >
                              <TableCell>
                                <Text weight="medium">{trailer.type.name}</Text>
                                <Text size="xs" color="muted">
                                  {trailer.load_type.name}
                                </Text>
                              </TableCell>
                              <TableCell>
                                <Text size="xs" color="muted" block>
                                  {trailer.model.name}
                                </Text>
                              </TableCell>
                              {canEditTrailer ? (
                                <TableCell>
                                  <Button
                                    color="neutral"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      openDialog("trailer", trailer.id);
                                    }}
                                  >
                                    {tCommon("edit")}
                                  </Button>
                                </TableCell>
                              ) : null}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableViewport>
                  )}
                </VStack>
              ) : null}
            </VStack>
          )}
        </Card>
      </MasterDetailLayout>

      <TruckModal
        open={truckModalOpen}
        onClose={closeDialog}
        truckId={truckModalId}
        transportCompanyId={selectedCarrierId || null}
        onSaved={() => invalidateFleet()}
      />

      <DriverModal
        open={driverModalOpen}
        onClose={closeDialog}
        driverId={driverModalId}
        transportCompanyId={selectedCarrierId || null}
        onSaved={() => invalidateFleet()}
      />

      <TrailerModal
        open={trailerModalOpen}
        onClose={closeDialog}
        trailerId={trailerModalId}
        transportCompanyId={selectedCarrierId || null}
        onSaved={() => invalidateFleet()}
      />

      <AdaptiveSheet
        open={isCarrierModalOpen}
        onDismiss={() => closeDialog()}
        onSubmit={handleSaveCarrier}
        size="lg"
      >
        <ModalHeader>
          <Value size="lg" weight="semibold">
            {isEditingCarrier
              ? tCarriers("editCarrier")
              : tCarriers("addCarrier")}
          </Value>
        </ModalHeader>
        <ModalBody>
          {carrierFormLoading ? (
            <Surface border="default" padding="lg">
              <VStack>
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-24 w-full" />
              </VStack>
            </Surface>
          ) : (
            <VStack gap="lg">
              <VStack gap="sm">
                <Label size="sm">{tCommon("name")}</Label>
                <Input
                  value={carrierForm.name}
                  onChange={(e) =>
                    setCarrierForm((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  autoComplete="organization"
                  fullWidth
                  disabled={carrierFormSaving || carrierFormLoading}
                />
              </VStack>

              <VStack gap="sm">
                <Label size="sm">{tCommon("email")}</Label>
                <EmailInput
                  data-testid="carrier-email-input"
                  value={carrierForm.email}
                  onChange={(value) =>
                    setCarrierForm((prev) => ({
                      ...prev,
                      email: value,
                    }))
                  }
                  autoComplete="email"
                  disabled={carrierFormSaving || carrierFormLoading}
                />
              </VStack>

              <VStack gap="sm">
                <Label size="sm">{tCommon("phone")}</Label>
                <PhoneInput
                  value={carrierForm.phone}
                  onChange={(value) =>
                    setCarrierForm((prev) => ({
                      ...prev,
                      phone: value,
                    }))
                  }
                  name="phone"
                  autoComplete="tel"
                  countries={carrierFormCountries}
                  loadingCountries={carrierFormCountriesLoading}
                />
              </VStack>

              <FormGrid>
                <CustomSelect
                  value={carrierForm.countryId}
                  onChange={(value) =>
                    setCarrierForm((prev) => ({
                      ...prev,
                      countryId: value,
                      cityId: "",
                    }))
                  }
                  fullWidth
                  disabled={carrierFormSaving || carrierFormLoading}
                  options={carrierCountryOptions}
                  label={tCommon("country")}
                  labelPosition="above"
                />

                <CustomSelect
                  value={carrierForm.cityId}
                  onChange={(value) =>
                    setCarrierForm((prev) => ({
                      ...prev,
                      cityId: value,
                    }))
                  }
                  fullWidth
                  disabled={
                    carrierFormSaving ||
                    carrierFormLoading ||
                    !carrierForm.countryId ||
                    carrierFormCitiesLoading
                  }
                  options={carrierCityOptions}
                  label={tCommon("city")}
                  labelPosition="above"
                />
              </FormGrid>

              <VStack gap="sm">
                <Label size="sm">{tCarriers("address")}</Label>
                <Textarea
                  value={carrierForm.address}
                  onChange={(e) =>
                    setCarrierForm((prev) => ({
                      ...prev,
                      address: e.target.value,
                    }))
                  }
                  fullWidth
                  disabled={carrierFormSaving || carrierFormLoading}
                />
              </VStack>

              <VStack gap="sm">
                <Label size="sm">{tCarriers("incorporationDate")}</Label>
                <DatePicker
                  value={carrierForm.incorporationDate}
                  onChange={(value) =>
                    setCarrierForm((prev) => ({
                      ...prev,
                      incorporationDate: value,
                    }))
                  }
                  disabled={carrierFormSaving || carrierFormLoading}
                />
              </VStack>

              {carrierFormError ? (
                <Text size="xs" color="danger">
                  {carrierFormError}
                </Text>
              ) : null}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="neutral"
            variant="outline"
            onClick={() => closeDialog()}
            disabled={carrierFormSaving}
          >
            {tCommon("cancel")}
          </Button>
          <Button
            onClick={handleSaveCarrier}
            disabled={
              carrierFormSaving ||
              carrierFormLoading ||
              !carrierForm.name ||
              !carrierForm.countryId ||
              !carrierForm.cityId ||
              !carrierForm.address ||
              !carrierForm.incorporationDate
            }
          >
            {carrierFormSaving ? <Icon.Spinner size="sm" /> : null}
            {tCommon("save")}
          </Button>
        </ModalFooter>
      </AdaptiveSheet>

      {isCompact &&
        (() => {
          if (selectedCarrierId) {
            if (activeTab === "fleet" && canAddTruck)
              return (
                <Fab
                  onClick={() => openDialog("truck")}
                  aria-label={tFleet("addTruck")}
                  data-testid="carriers-detail-fab"
                />
              );
            if (activeTab === "drivers" && canAddDriver)
              return (
                <Fab
                  onClick={() => openDialog("driver")}
                  aria-label={tFleet("addDriver")}
                  data-testid="carriers-detail-fab"
                />
              );
            if (activeTab === "trailers" && canAddTrailer)
              return (
                <Fab
                  onClick={() => openDialog("trailer")}
                  aria-label={tFleet("addTrailer")}
                  data-testid="carriers-detail-fab"
                />
              );
            return null;
          }
          if (canAddCarrier)
            return (
              <Fab
                onClick={openCreateModal}
                aria-label={tCarriers("addCarrier")}
                data-testid="carriers-fab"
              />
            );
          return null;
        })()}
    </VStack>
  );
}

export const Route = createFileRoute("/_app/carriers")({
  component: CarriersPage,
});
